import { useEffect, useRef, useState } from "react";
import { useVoiceSearch } from "../core/useVoiceSearch";
import { VoiceCriteria, VocabularyResponse } from "../core/types";
import { WebAudioCapture } from "./WebAudioCapture";
import { voiceHttpDeps, voiceTranscribeHttpDeps } from "./voiceHttp";
import { getVocabulary } from "./vocabularyClient";
import { isVersionSupported } from "./isVersionSupported";
import { VOICE_CLIENT_VERSION } from "./constants";
import { purgeLegacyModelCache } from "./legacyModelCache";

/**
 * Web wiring for useVoiceSearch: constructs the web AudioCapture once per
 * mount, fetches /Voice/Vocabulary once (feature gate + confidence threshold
 * + hints + transcript cap), and keeps the deps object identity stable across
 * renders (the underlying machine is re-created whenever the deps object
 * identity changes).
 *
 * No `recognizer` is supplied: speech-to-text runs server-side via
 * POST /Voice/Transcribe. The machine treats a missing recognizer as the
 * server path, so nothing on web downloads or initialises a model.
 */
export function useWebVoiceSearch(getExistingCriteria: () => Partial<VoiceCriteria>) {
  const [vocabulary, setVocabulary] = useState<VocabularyResponse | null>(null);
  const [vocabularyLoaded, setVocabularyLoaded] = useState(false);

  // Reclaim the ~31 MB the old on-device model left in Cache Storage.
  // Runs once per mount, off the critical path.
  useEffect(() => {
    purgeLegacyModelCache();
  }, []);

  useEffect(() => {
    let cancelled = false;
    getVocabulary().then((value) => {
      if (!cancelled) {
        setVocabulary(value);
        setVocabularyLoaded(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const audioCaptureRef = useRef<WebAudioCapture | null>(null);
  if (!audioCaptureRef.current) audioCaptureRef.current = new WebAudioCapture();

  const vocabularyRef = useRef<VocabularyResponse | null>(vocabulary);
  vocabularyRef.current = vocabulary;

  const depsRef = useRef<ReturnType<typeof buildDeps> | null>(null);
  if (!depsRef.current) {
    depsRef.current = buildDeps(audioCaptureRef.current, getExistingCriteria, vocabularyRef);
  } else {
    // getExistingCriteria closes over form state that changes every render —
    // keep the deps object identity stable while always calling the latest.
    depsRef.current.getExistingCriteria = getExistingCriteria;
  }

  const voiceSearch = useVoiceSearch(depsRef.current);

  // Feature gate per the Vocabulary contract: enabled === false, or this
  // build older than minSupportedClientVersion → hide the mic silently.
  // Don't call Interpret in that case; it 503s. Fail OPEN (show the mic)
  // if the gate check itself fails — never let an optimisation block voice.
  const micVisible =
    !vocabularyLoaded ||
    vocabulary === null ||
    (vocabulary.enabled &&
      isVersionSupported(VOICE_CLIENT_VERSION, vocabulary.minSupportedClientVersion));

  /**
   * Kept as a no-op so callers (the mic button's pointerdown) don't have to
   * change. There is nothing left to warm: the model that this used to
   * pre-fetch now lives on the server.
   */
  const warmUp = () => {};

  // Guards against an accidental double-tap firing two permission sequences
  // back to back — not the server's 30/hr rate limit (that's enforced
  // server-side regardless), just a client-side debounce. It matters more on
  // the server path: each run now costs a paid transcription.
  const lastStartAtRef = useRef(0);
  const START_COOLDOWN_MS = 1200;
  const start = () => {
    const now = Date.now();
    if (now - lastStartAtRef.current < START_COOLDOWN_MS) return;
    lastStartAtRef.current = now;
    voiceSearch.start();
  };

  return { ...voiceSearch, start, warmUp, micVisible, vocabularyLoaded };
}

function buildDeps(
  audioCapture: WebAudioCapture,
  getExistingCriteria: () => Partial<VoiceCriteria>,
  vocabularyRef: { current: VocabularyResponse | null },
) {
  return {
    audioCapture,
    http: voiceHttpDeps,
    transcribeHttp: voiceTranscribeHttpDeps,
    getExistingCriteria,
    languageHint: () => navigator.language.split("-")[0] || "en",
    clientVersion: VOICE_CLIENT_VERSION,
    getConfidenceThreshold: () => vocabularyRef.current?.confidenceThreshold,
    getMaxTranscriptChars: () => vocabularyRef.current?.maxTranscriptChars,
  };
}
